import React, { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import TextInput from '../../../BaseComponents/TextInput';
import { Formik } from 'formik';
import Button from '../../../BaseComponents/Button';
import { ReactComponent as TrashIcon } from '../../../../assets/icons/TrashBin2.svg';
// import './MemberNotes.scss';
import { formatInTimeZone } from 'date-fns-tz';
import Select, { SelectOption } from '../../../BaseComponents/Select';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
// import 'trix/dist/trix.css';
import 'trix';
import {
  changeDeleteNoteModalVisibility,
  changeSaveDataNoteModalVisibility,
  setNotesList,
  setNoteDataToEdit,
  changeNotesModalVisibility
} from '../../../../store/memberSlice';
import { setNotesCount } from '../../../../store/appSlice';
import { getNotesCount } from '../../../../api/counters';
import { setAlertsCount } from '../../../../store/alertsSlice';
import { getUnprocessedTestResultsCount } from '../../../../api/testResults';
import {
  createMemberNote,
  deleteNote,
  updateMemberNote
} from '../../../../api/members';

import { TaggedUser } from '../../../../api/staff';
import VoiceNotes, { VoiceNote } from './VoiceNotes';
import { putFile } from '../../../../api/fileStorage';
import { getFullImageUrl, NOTE_TEMPLATE_CATEGORIES, notesCategory } from '../../../../utils/constants';
import { waitForMinModalLoaderVisible } from '../../../../utils/modalLoaderMinVisible';
import { AudioAttachment, NoteResponse, CreateNoteRequest, EditNoteRequest, getNoteById } from '../../../../api/notes';
import Loader from '../../../BaseComponents/Loader';
import { NoteTemplate, getNoteTemplatesList } from '../../../../api/note-templates';
import TaggedUserDropdown from './TaggedUserDropdown';

const CustomTrixEditor = React.forwardRef<{ insertTag: (user: TaggedUser) => void }, {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  onTagDetect?: (query: string, position: { top: number; left: number }) => void;
  onTagClose?: () => void;
}>(({ value, onChange, placeholder, className, readOnly = false, onTagDetect, onTagClose }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;
  const inputId = useMemo(() => `trix-input-${Math.random().toString(36).substr(2, 9)}`, []);

  React.useImperativeHandle(ref, () => ({
    insertTag: (user: TaggedUser) => {
      const editor = editorRef.current;
      if (!editor) return;
      const trixEditor = editor.querySelector('trix-editor') as any;
      if (!trixEditor) return;

      const attachment = new (window as any).Trix.Attachment({
        content: `<span class="tagged-user" data-user-id="${user.id}">@${user.display_name}</span>`
      });

      const range = trixEditor.editor.getSelectedRange();
      const text = trixEditor.editor.getDocument().toString();
      const textBefore = text.substring(0, range[0]);
      const lastAt = textBefore.lastIndexOf('@');

      if (lastAt !== -1) {
        trixEditor.editor.setSelectedRange([lastAt, range[0]]);
        trixEditor.editor.insertAttachment(attachment);
        trixEditor.editor.insertString(" ");
      }
    }
  }));

  useEffect(() => {
    const input = inputRef.current;
    const editor = editorRef.current;
    if (!input || !editor) return;

    const trixEditor = document.createElement('trix-editor');
    trixEditor.setAttribute('input', inputId);
    if (placeholder) {
      trixEditor.setAttribute('placeholder', placeholder);
    }
    if (readOnly) {
      trixEditor.setAttribute('contenteditable', 'false');
    }

    editor.innerHTML = '';
    editor.appendChild(trixEditor);
    if (readOnly) {
      editor.classList.add('trix-editor-wrapper--readonly');
    } else {
      editor.classList.remove('trix-editor-wrapper--readonly');
    }

    input.value = valueRef.current || '';
    // Use type assertion safely to access editor property
    const element = trixEditor as unknown as { editor: { loadHTML: (html: string) => void; insertHTML: (html: string) => void } };
    element.editor.loadHTML(valueRef.current || '');

    // Handle file attachments - upload images to server and get full S3 URL
    const handleFileAccept = async (event: any) => {
      if (readOnly) {
        event.preventDefault();
        return;
      }

      const file = event.file;
      if (!file || !file.type.startsWith('image/')) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      try {
        const uploadResponse = await putFile({ file: file, file_type: file.type });

        if (!uploadResponse || (!uploadResponse.full_key && !uploadResponse.url)) {
          alert('Failed to upload image. Please try again.');
          return;
        }

        // Determine the correct key or URL to use
        const key = uploadResponse.full_key || (uploadResponse.url ? extractKeyFromUrl(uploadResponse.url) : null);

        if (!key) {
          alert('Failed to upload image. Please try again.');
          return;
        }

        const fullImageUrl = getFullImageUrl(key);
        const imgHtml = `<img src="${fullImageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;">`;
        element.editor.insertHTML(imgHtml);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };

    const handleChange = () => {
      if (readOnly) return;
      onChangeRef.current(trixEditor.innerHTML);
    };

    const handleSelectionChange = () => {
      if (readOnly || !onTagDetect || !onTagClose) return;

      const editor = (trixEditor as any).editor;
      const range = editor.getSelectedRange();

      // Only handle collapsed selections (caret position)
      if (range[0] !== range[1]) {
        onTagClose();
        return;
      }

      const cursorPosition = range[0];
      const text = editor.getDocument().toString();

      // Look back from cursor to find @
      const textBeforeCursor = text.substring(0, cursorPosition);
      const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtSymbolIndex !== -1) {
        const query = textBeforeCursor.substring(lastAtSymbolIndex + 1);

        // ensure no spaces in query (simple tag detection)
        if (!query.includes(' ') && !query.includes('\n')) {
          const selectionRect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
          const container = editorRef.current?.closest('.textarea-container');

          if (selectionRect && container) {
            const containerRect = container.getBoundingClientRect();
            onTagDetect(query, {
              top: selectionRect.bottom - containerRect.top,
              left: selectionRect.left - containerRect.left
            });
            return;
          }
        }
      }

      onTagClose();
    };

    trixEditor.addEventListener('trix-file-accept', handleFileAccept);
    trixEditor.addEventListener('trix-change', handleChange);
    trixEditor.addEventListener('trix-selection-change', handleSelectionChange);
    trixEditor.addEventListener('keyup', handleSelectionChange);

    return () => {
      trixEditor.removeEventListener('trix-file-accept', handleFileAccept);
      trixEditor.removeEventListener('trix-change', handleChange);
      trixEditor.removeEventListener('trix-selection-change', handleSelectionChange);
      trixEditor.removeEventListener('keyup', handleSelectionChange);
    };
  }, [inputId, placeholder, readOnly, onTagDetect, onTagClose]);

  // Handle external value changes (e.g. template selection)
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const trixEditor = editor.querySelector('trix-editor');
    if (!trixEditor) return;

    // Only update if the value is different and we're not currently editing (to avoid cursor jumps)
    // When selecting a template, the editor won't be the active element.
    if (value !== trixEditor.innerHTML && document.activeElement !== trixEditor) {
      const element = trixEditor as unknown as { editor: { loadHTML: (html: string) => void } };
      element.editor.loadHTML(value || '');
    }
  }, [value]);

  return (
    <div className={className}>
      <input ref={inputRef} id={inputId} type="hidden" />
      <div ref={editorRef} />
    </div>
  );
});




const extractKeyFromUrl = (urlOrKey: string): string => {
  if (!urlOrKey) return '';

  if (!urlOrKey.startsWith('http://') && !urlOrKey.startsWith('https://')) {
    return urlOrKey;
  }

  try {
    const url = new URL(urlOrKey);
    let path = url.pathname;
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    return path || urlOrKey;
  } catch (error) {
    const s3Pattern = /s3\.([^.]+)\.amazonaws\.com\/(.+)/;
    const match = urlOrKey.match(s3Pattern);
    if (match && match[2]) {
      return match[2];
    }

    return urlOrKey;
  }
};

const extractMentionedUserIds = (description?: string): string[] => {
  if (!description) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(description, 'text/html');
  const mentionedUsers = new Set<string>();

  const collectMentionedIds = (root: ParentNode) => {
    const taggedElements = root.querySelectorAll('.tagged-user[data-user-id]');
    taggedElements.forEach((el) => {
      const userId = el.getAttribute('data-user-id');
      if (userId) {
        mentionedUsers.add(userId);
      }
    });
  };

  collectMentionedIds(doc);

  const trixAttachmentElements = doc.querySelectorAll('[data-trix-attachment]');
  trixAttachmentElements.forEach((el) => {
    const trixAttachment = el.getAttribute('data-trix-attachment');
    if (!trixAttachment) return;

    try {
      const attachmentData = JSON.parse(trixAttachment) as { content?: string };
      if (!attachmentData.content) return;

      const contentDoc = parser.parseFromString(attachmentData.content, 'text/html');
      collectMentionedIds(contentDoc);
    } catch (error) {
    }
  });

  return Array.from(mentionedUsers);
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  forceViewOnly?: boolean;
  initialMode?: 'create' | 'update' | 'view';
  isReferralLetter?: boolean;
};

const MemberNotesModal = ({ isOpen, onClose, forceViewOnly = false, initialMode, isReferralLetter = false }: Props) => {
  const dispatch = useDispatch();
  const [modalError, setModalError] = useState<any>(false);
  const [modalMode, setModalModal] = useState<'update' | 'view' | 'create'>(
    forceViewOnly ? 'view' : 'create'
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [voiceNotes, setVoiceNotes] = useState<VoiceNote[]>([]);
  const [voiceNoteToDelete, setVoiceNoteToDelete] = useState<string | null>(null);
  const [isDeleteVoiceNoteModalOpened, setIsDeleteVoiceNoteModalOpened] = useState(false);

  // Tagging state
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagDropdownPosition, setTagDropdownPosition] = useState({ top: 0, left: 0 });
  const trixEditorRef = useRef<{ insertTag: (user: TaggedUser) => void }>(null);

  const handleTagClose = useCallback(() => {
    setShowTagDropdown(false);
    setTagSearchQuery('');
  }, []);

  const handleTagDetect = useCallback((query: string, position: { top: number; left: number }) => {
    setTagSearchQuery(query);
    setTagDropdownPosition(position);
    setShowTagDropdown(true);
  }, []);

  const handleInsertTag = useCallback((user: TaggedUser) => {
    if (trixEditorRef.current) {
      trixEditorRef.current.insertTag(user);
    }
    handleTagClose();
  }, [handleTagClose]);
  const [noteData, setNoteData] = useState<NoteResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [noteTemplates, setNoteTemplates] = useState<NoteTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const categoryLabelMap = useMemo(
    () =>
      NOTE_TEMPLATE_CATEGORIES.reduce<Record<string, string>>((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {}),
    []
  );

  const fetchNoteTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const pageSize = 100;
      let offset = 0;
      let collected: NoteTemplate[] = [];
      let total = Infinity;

      while (collected.length < total) {
        const res = await getNoteTemplatesList(undefined, pageSize, offset);
        const pageTemplates = res?.note_templates || [];
        total = res?.total_count ?? pageTemplates.length;
        collected = [...collected, ...pageTemplates];
        offset += pageSize;

        if (pageTemplates.length === 0) {
          break;
        }
      }

      setNoteTemplates(collected);
    } catch (error) {
      console.error('Error fetching note templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const refreshAlertsCount = async () => {
    try {
      const alerts = await getUnprocessedTestResultsCount();
      dispatch(setAlertsCount(alerts.count));
    } catch (error) {
      console.log('Error refreshing alerts count', error);
    }
  };

  const refreshNotesCount = async () => {
    try {
      const notes = await getNotesCount();
      dispatch(setNotesCount(notes.total_count));
    } catch (error) {
      console.log('Error refreshing notes count', error);
    }
  };

  const refreshBothCounts = async () => {
    try {
      await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
    } catch (error) {
      console.warn('Failed to refresh counts:', error);
    }
  };

  const {
    noteDataToEdit,
    isDeleteNoteModalOpened,
    isSaveDataNoteModalOpened,
    memberData,
    notesList
  } = useSelector((state: RootState) => state.member);
  const { admin } = useSelector((state: RootState) => state.admin);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const isReadOnly = admin.role === "doctor" && !assigned_doctor;
  const canEditAnyNote = ['admin', 'superadmin'].includes((admin?.role || '').toLowerCase());
  const shouldForceViewOnly = forceViewOnly || Boolean((noteDataToEdit as any)?.force_view_only);

  const resolveModalMode = useCallback((): 'update' | 'view' | 'create' => {
    if (initialMode === 'view') {
      return 'view';
    }

    if (initialMode === 'create') {
      return isReadOnly ? 'view' : 'create';
    }

    if (noteDataToEdit) {
      if (shouldForceViewOnly) {
        return 'view';
      }
      if ((!canEditAnyNote && noteDataToEdit.author.id !== admin.user_id) || isReadOnly) {
        return 'view';
      }
      return 'update';
    }
    if (shouldForceViewOnly || isReadOnly) {
      return 'view';
    }
    return 'create';
  }, [admin.user_id, canEditAnyNote, initialMode, isReadOnly, noteDataToEdit, shouldForceViewOnly]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      return;
    }
    const mode = resolveModalMode();
    setModalModal(mode);
    if (noteDataToEdit?.id && (mode === 'view' || mode === 'update')) {
      setIsLoading(true);
    }
  }, [isOpen, resolveModalMode, noteDataToEdit?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchNoteTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchNoteDetails = async () => {
      if (!noteDataToEdit?.id) {
        setNoteData(null);
        setVoiceNotes([]);
        setModalModal(resolveModalMode());
        return;
      }

      const applyMinLoaderTime = resolveModalMode() === 'view';
      const loaderStartedAt = Date.now();
      setIsLoading(true);
      setModalError(false);
      setVoiceNoteToDelete(null);
      setIsDeleteVoiceNoteModalOpened(false);
      setVoiceNotes([]);

      try {
        const response = await getNoteById(noteDataToEdit.id);
        setNoteData(response);

        const audioAttachments = response.audio_attachments || response.attachments;
        const audioUrls = response.audio_urls;

        if (audioAttachments && Array.isArray(audioAttachments) && audioAttachments.length > 0) {
          const existingVoiceNotes = audioAttachments.map((attachment: AudioAttachment, index: number) => {
            // If attachment.url is already a full URL (starts with http/https), use it directly
            // Otherwise, construct the URL from the key
            let audioUrl: string;
            let audioUrlKey: string;

            if (attachment.url && (attachment.url.startsWith('http://') || attachment.url.startsWith('https://'))) {
              // Use the provided full URL directly (e.g., S3 signed URLs)
              audioUrl = attachment.url;
              audioUrlKey = attachment.key || extractKeyFromUrl(attachment.url);
              console.log('Using provided full URL for audio attachment:', {
                index,
                url: audioUrl,
                key: audioUrlKey,
                duration: attachment.duration
              });
            } else {
              // Construct URL from key
              audioUrlKey = attachment.key || (attachment.url ? extractKeyFromUrl(attachment.url) : '');
              const fullUrl = getFullImageUrl(audioUrlKey);
              audioUrl = fullUrl || audioUrlKey || attachment.url || '';
              console.log('Constructed URL for audio attachment:', {
                index,
                url: audioUrl,
                key: audioUrlKey,
                duration: attachment.duration
              });
            }

            return {
              id: `existing-${index}-${Date.now()}`,
              duration: attachment.duration || 0,
              audioUrl,
              audioUrlKey: audioUrlKey
            } as VoiceNote & { audioUrlKey?: string };
          });

          console.log('Setting voice notes:', existingVoiceNotes);
          setVoiceNotes(existingVoiceNotes);
        } else if (audioUrls && Array.isArray(audioUrls) && audioUrls.length > 0) {
          const existingVoiceNotesPromises = audioUrls.map(async (urlOrKey: string, index: number) => {
            // If urlOrKey is already a full URL (starts with http/https), use it directly
            // Otherwise, construct the URL from the key
            let audioUrl: string;
            let audioUrlKey: string;

            if (urlOrKey && (urlOrKey.startsWith('http://') || urlOrKey.startsWith('https://'))) {
              // Use the provided full URL directly (e.g., S3 signed URLs)
              audioUrl = urlOrKey;
              audioUrlKey = extractKeyFromUrl(urlOrKey);
            } else {
              // Construct URL from key
              audioUrlKey = extractKeyFromUrl(urlOrKey);
              const fullUrl = getFullImageUrl(audioUrlKey);
              audioUrl = fullUrl || audioUrlKey || urlOrKey;
            }

            let duration = 0;
            try {
              const audio = new Audio(audioUrl);
              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Timeout')), 5000);
                const handleLoadedMetadata = () => {
                  clearTimeout(timeout);
                  duration = Math.round(audio.duration);
                  if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
                    duration = 0;
                  }
                  audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                  audio.removeEventListener('error', handleError);
                  resolve();
                };
                const handleError = () => {
                  clearTimeout(timeout);
                  audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                  audio.removeEventListener('error', handleError);
                  resolve();
                };
                audio.addEventListener('loadedmetadata', handleLoadedMetadata);
                audio.addEventListener('error', handleError);
                audio.load();
              });
            } catch (error) {
              console.warn('Error loading audio duration:', error);
            }

            return {
              id: `existing-${index}-${Date.now()}`,
              duration,
              audioUrl,
              audioUrlKey: audioUrlKey
            } as VoiceNote & { audioUrlKey?: string };
          });

          const existingVoiceNotes = await Promise.all(existingVoiceNotesPromises);
          setVoiceNotes(existingVoiceNotes);
        }
      } catch (error) {
        console.error('Error fetching note:', error);
        setModalError(true);
      } finally {
        if (applyMinLoaderTime) {
          await waitForMinModalLoaderVisible(loaderStartedAt);
        }
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchNoteDetails();
    }
  }, [isOpen, noteDataToEdit?.id, isReadOnly, shouldForceViewOnly, resolveModalMode]);



  const getModalTitle = () => {
    const titleBase = isReferralLetter ? 'Referral letter' : 'Note';
    return modalMode === 'view'
      ? `View ${titleBase}`
      : modalMode === 'update'
        ? `Edit ${titleBase}`
        : `Add ${titleBase}`;
  };

  const onDeleteNote = async () => {
    try {
      await deleteNote({
        note_id: noteDataToEdit?.id!
      });
      dispatch(changeDeleteNoteModalVisibility(false));
      dispatch(
        setNotesList(
          notesList!.filter((note) => note.id !== noteDataToEdit?.id)
        )
      );
      await refreshBothCounts();
      dispatch(setNoteDataToEdit(null));
      dispatch(changeNotesModalVisibility(false));
      onClose();
    } catch (error: any) {
      console.error('Error deleting note:', error);
    }
  };

  const onSaveDataNote = async (values: { title: any; description: any; is_private: boolean; category?: string; note_template_id?: number | '' }) => {
    const taggedUsers = extractMentionedUserIds(values.description);

    const uploadPromises = voiceNotes.map(async (voiceNote) => {
      if (voiceNote.audioUrl.startsWith('blob:') && voiceNote.audioBlob) {
        try {
          const extension = 'm4a';
          const mimeType = 'audio/mp4';

          const audioFile = new File([voiceNote.audioBlob], `voice-${voiceNote.id}.${extension}`, {
            type: mimeType
          });
          const uploadResponse = await putFile({ file: audioFile, file_type: mimeType });
          const key = uploadResponse.full_key || uploadResponse.url || audioFile.name;
          return { key, duration: voiceNote.duration };
        } catch (error) {
          console.error('Error uploading voice note:', error);
          return null;
        }
      } else {
        const noteWithKey = voiceNote as VoiceNote & { audioUrlKey?: string };
        // Extract key from URL if audioUrlKey is not available or if it's a full URL
        const urlOrKey = noteWithKey.audioUrlKey || voiceNote.audioUrl;
        const key = extractKeyFromUrl(urlOrKey);
        return { key, duration: voiceNote.duration };
      }
    });

    const uploadedAttachments = await Promise.all(uploadPromises);
    const validAudioAttachments = uploadedAttachments.filter((attachment): attachment is { key: string; duration: number } => attachment !== null);

    const requestObj: EditNoteRequest = {
      note_id: noteDataToEdit?.id!,
      title: values.title ?? '',
      description: values.description,
      is_private: values.is_private ?? false,
      category: values.category || 'regular',
      note_template_id: values.note_template_id || undefined,
      is_referral_letter: isReferralLetter,
      mentioned_user_ids: taggedUsers,
      ...(validAudioAttachments.length > 0 && { audio_attachments: validAudioAttachments }),
    };
    try {
      await updateMemberNote(requestObj);

      // voiceNotes.forEach(note => {
      //   if (note.audioUrl.startsWith('blob:')) {
      //     URL.revokeObjectURL(note.audioUrl);
      //   }
      // });
      // setVoiceNotes([]);
      dispatch(changeSaveDataNoteModalVisibility(false));
      await refreshBothCounts();
      onClose();
    } catch (error: any) {
      console.error('Error updating note:', error);
    }
  };

  const onCloseModalClick = () => {
    onClose();
  };



  const handleAddVoiceNote = (voiceNote: VoiceNote) => {
    setVoiceNotes(prev => [...prev, voiceNote]);
  };

  const handleDeleteVoiceNote = (id: string) => {
    setVoiceNoteToDelete(id);
    setIsDeleteVoiceNoteModalOpened(true);
  };

  const confirmDeleteVoiceNote = () => {
    if (voiceNoteToDelete) {
      setVoiceNotes(prev => {
        const note = prev.find(n => n.id === voiceNoteToDelete);
        if (note && note.audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(note.audioUrl);
        }
        return prev.filter(n => n.id !== voiceNoteToDelete);
      });
      setVoiceNoteToDelete(null);
      setIsDeleteVoiceNoteModalOpened(false);
    }
  };



  const displayNoteData = noteData || (modalMode === 'create' ? noteDataToEdit : null);
  const templateOptions = useMemo(() => {
    const sortedTemplates = [...noteTemplates].sort((a, b) => {
      const labelA = `${categoryLabelMap[a.category] || a.category} - ${a.name}`.toLowerCase();
      const labelB = `${categoryLabelMap[b.category] || b.category} - ${b.name}`.toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return [
      { key: '', value: 'No template' },
      ...sortedTemplates.map((template) => ({
        key: template.id,
        value: `${categoryLabelMap[template.category] || template.category} - ${template.name}`
      }))
    ];
  }, [categoryLabelMap, noteTemplates]);
  const existingTemplateId = displayNoteData ? (displayNoteData as any).note_template_id ?? '' : '';
  const existingCategory = displayNoteData
    ? (displayNoteData as any).category ?? ((displayNoteData as any).is_fbs_note ? 'fbs_note' : 'regular')
    : 'regular';
  const originalIsPrivate = displayNoteData ? (displayNoteData.is_private ?? false) : false;
  const canShowFbsCheckbox = ['admin', 'doctor', 'nutritionist', 'superadmin'].includes(
    (admin?.role || '').toLowerCase()
  );
  const isViewMode = modalMode === 'view' || shouldForceViewOnly;
  const showLoader = isLoading && (modalMode === 'view' || modalMode === 'update');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onCloseModalClick()}
      title={getModalTitle()}
      width='640px'
    >
      {showLoader ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 240
          }}
        >
          <Loader height='120px' />
        </div>
      ) : (
        <Formik
          enableReinitialize={true}
          key={noteData?.id || 'new'}
          initialValues={{
            title: displayNoteData ? (displayNoteData.title || '') : '',
            description: displayNoteData ? displayNoteData.description : '',
            is_private: displayNoteData ? (displayNoteData.is_private ?? false) : false,
            category: existingCategory,
            note_template_id: displayNoteData ? existingTemplateId : ''
          }}
          onSubmit={async (values) => {
            const taggedUsers = extractMentionedUserIds(values.description);

            const uploadPromises = voiceNotes.map(async (voiceNote) => {
              if (voiceNote.audioUrl.startsWith('blob:') && voiceNote.audioBlob) {
                try {
                  const extension = 'm4a';
                  const mimeType = 'audio/mp4';

                  const audioFile = new File([voiceNote.audioBlob], `voice-${voiceNote.id}.${extension}`, {
                    type: mimeType
                  });
                  const uploadResponse = await putFile({ file: audioFile, file_type: mimeType });
                  const key = uploadResponse.full_key || uploadResponse.url || audioFile.name;
                  return { key, duration: voiceNote.duration };
                } catch (error) {
                  console.error('Error uploading voice note:', error);
                  return null;
                }
              } else {
                const noteWithKey = voiceNote as VoiceNote & { audioUrlKey?: string };
                // Extract key from URL if audioUrlKey is not available or if it's a full URL
                const urlOrKey = noteWithKey.audioUrlKey || voiceNote.audioUrl;
                const key = extractKeyFromUrl(urlOrKey);
                return { key, duration: voiceNote.duration };
              }
            });

            const uploadedAttachments = await Promise.all(uploadPromises);
            const validAudioAttachments = uploadedAttachments.filter((attachment): attachment is { key: string; duration: number } => attachment !== null);

            setLoading(true);
            try {
              let resp: NoteResponse;

              if (modalMode === 'update') {
                const updateRequest: EditNoteRequest = {
                  note_id: noteDataToEdit?.id!,
                  title: values.title ?? '',
                  description: values.description,
                  is_private: values.is_private ?? false,
                  category: values.category || 'regular',
                  note_template_id: values.note_template_id || undefined,
                  is_referral_letter: isReferralLetter,
                  mentioned_user_ids: taggedUsers,
                  ...(validAudioAttachments.length > 0 && { audio_attachments: validAudioAttachments }),
                };
                resp = await updateMemberNote(updateRequest) as NoteResponse;
              } else {
                const createRequest: CreateNoteRequest = {
                  title: values.title ?? '',
                  description: values.description,
                  member_id: memberData?.user_id!,
                  is_private: values.is_private ?? false,
                  category: values.category || 'regular',
                  note_template_id: values.note_template_id || undefined,
                  is_referral_letter: isReferralLetter,
                  mentioned_user_ids: taggedUsers,
                  ...(validAudioAttachments.length > 0 && { audio_attachments: validAudioAttachments }),
                };
                resp = await createMemberNote(createRequest) as NoteResponse;
              }

              const responseWithAudio = resp as NoteResponse;
              const audioData = responseWithAudio.audio_attachments || responseWithAudio.attachments
                ? { audio_attachments: responseWithAudio.audio_attachments || responseWithAudio.attachments }
                : { audio_attachments: validAudioAttachments };

              if (modalMode === 'create') {
                dispatch(
                  setNotesList([
                    {
                      ...resp,
                      is_private: values.is_private ?? false,
                      category: values.category || 'regular',
                      note_template_id: values.note_template_id || undefined,
                      is_referral_letter: isReferralLetter,
                      ...audioData
                    },
                    ...(notesList || [])
                  ])
                );
              } else if (modalMode === 'update') {
                dispatch(
                  setNotesList(
                    (notesList || []).map((note) => {
                      if (note.id === noteDataToEdit?.id) {
                        return {
                          ...note,
                          title: values.title ? values.title : '',
                          description: values.description,
                          is_private: values.is_private ?? false,
                          category: values.category || 'regular',
                          note_template_id: values.note_template_id || undefined,
                          is_referral_letter: isReferralLetter,
                          ...audioData
                        };
                      }
                      return note;
                    })
                  )
                );
              }

              // voiceNotes.forEach(note => {
              //   if (note.audioUrl.startsWith('blob:')) {
              //     URL.revokeObjectURL(note.audioUrl);
              //   }
              // });
              // setVoiceNotes([]);
              setModalError(false);
              onClose();
              refreshBothCounts().catch(error => {
                console.warn('Failed to refresh counts after note operation:', error);
              });
            } catch (error: any) {
              console.error('Error creating/updating note:', error);
              if (error?.response?.status && error.response.status >= 400 && error.response.status < 500) {
                setModalError(true);
              } else if (error?.message && error.message.includes('Network Error')) {
                setModalError(true);
              } else {
                console.warn('Note operation completed with non-critical warning:', error);
              }
            } finally {
              setLoading(false);
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit }) => {
            const selectedTemplateOption =
              templateOptions.find((opt) => opt.key === values.note_template_id) ||
              templateOptions[0] ||
              { key: '', value: '' };
            const selectedCategoryOption =
              notesCategory.find((opt) => opt.key === values.category) ||
              notesCategory.find((opt) => opt.key === 'regular') ||
              notesCategory[0];

            const handleTemplateSelect = (option: SelectOption) => {
              setFieldValue('note_template_id', option.key);
              if (!option.key) {
                return;
              }
              const template = noteTemplates.find((t) => t.id === option.key);
              if (template) {
                if (!values.title) {
                  setFieldValue('title', template.name);
                }
                // For Trix editor, we want to preserve the HTML content
                setFieldValue('description', template.template);
              }
            };

            return (
              <>
                {displayNoteData && (modalMode === 'update' || isViewMode) && (
                  <div className='note-author-info'>
                    <span>
                      {formatInTimeZone(
                        new Date(displayNoteData.created_at),
                        'UTC',
                        'dd/MM/yyyy'
                      )}
                    </span>
                    <span>
                      {formatInTimeZone(
                        new Date(displayNoteData.created_at),
                        'UTC',
                        'hh:mm a'
                      )}
                    </span>
                    <span>{`${displayNoteData.author.first_name} ${displayNoteData.author.last_name[0]}.`}</span>
                  </div>
                )}
                <form
                  onSubmit={(e) => {
                    if (isViewMode) {
                      e.preventDefault();
                      return;
                    }
                    handleSubmit(e);
                  }}
                  className={`member-notes-modal-form input-wrapper${isViewMode ? ' member-notes-modal-form--view' : ''}`}
                >
                  <div className='label-title'>Title</div>
                  <TextInput
                    name='title'
                    placeholder='Title (optional)'
                    value={values.title!}
                    onChange={(e) => setFieldValue('title', e.target.value)}
                    isDisabled={isViewMode}
                  />
                  <div className='label-title'>Templates</div>
                  <Select
                    name='note_template_id'
                    options={templateOptions}
                    selectedOption={selectedTemplateOption}
                    onChange={(option) => handleTemplateSelect(option)}
                    placeholder='Select template (optional)'
                    isError={false}
                    isLoading={templatesLoading}
                    isDisable={isReadOnly || isViewMode}
                    preventAutoOpenOnLoad={true}
                  />
                  <div className='label-title'>{isReferralLetter ? 'Description' : 'Note'} *</div>
                  <div className='textarea-container'>
                    <div
                      className={`trix-editor-wrapper${isViewMode ? ' trix-editor-wrapper--readonly' : ''}${isReferralLetter ? ' trix-editor-wrapper--referral-letter' : ''}`}
                    >
                      <CustomTrixEditor
                        ref={trixEditorRef}
                        value={values.description}
                        onChange={(html: string) => setFieldValue('description', html)}
                        placeholder={isReferralLetter ? "Enter Description" : "Enter Note"}
                        className="trix-editor"
                        readOnly={isViewMode}
                        onTagDetect={isViewMode ? undefined : handleTagDetect}
                        onTagClose={isViewMode ? undefined : handleTagClose}
                      />
                      {!isViewMode && (
                        <TaggedUserDropdown
                          isVisible={showTagDropdown}
                          position={tagDropdownPosition}
                          onSelectUser={handleInsertTag}
                          onClose={handleTagClose}
                          searchQuery={tagSearchQuery}
                        />
                      )}
                    </div>
                  </div>
                  {!isReferralLetter && (
                    <>
                      <div className='label-title'>Visibility</div>
                      <div className='radio-group'>
                        <label className='radio-label'>
                          <input
                            type='radio'
                            name='is_private'
                            checked={!values.is_private}
                            onChange={() => setFieldValue('is_private', false)}
                            disabled={isViewMode}
                          />
                          <span>Shared with Member</span>
                        </label>
                        <label className='radio-label'>
                          <input
                            type='radio'
                            name='is_private'
                            checked={values.is_private}
                            onChange={() => setFieldValue('is_private', true)}
                            disabled={isViewMode}
                          />
                          <span>Private</span>
                        </label>
                      </div>

                      {canShowFbsCheckbox && (
                        <>
                          <div className='label-title'>Category</div>
                          <Select
                            name='category'
                            options={notesCategory}
                            selectedOption={selectedCategoryOption}
                            onChange={(option) => setFieldValue('category', option.key)}
                            placeholder='Select category'
                            isError={false}
                            isDisable={isReadOnly || isViewMode}
                          />
                        </>
                      )}
                      <VoiceNotes
                        voiceNotes={voiceNotes}
                        onAddVoiceNote={handleAddVoiceNote}
                        onDeleteVoiceNote={handleDeleteVoiceNote}
                        isReadOnly={isReadOnly || isViewMode}
                      />
                    </>
                  )}
                  {!isViewMode && (
                    <div className='btn-wrapper'>
                      <Button
                        text={modalMode === 'update' ? 'Save' : `Add ${isReferralLetter ? 'Referral letter' : 'Note'}`}
                        type='submit'
                        isDisabled={
                          (!values.description) ||
                          !!(
                            modalMode === 'update' &&
                            displayNoteData &&
                            values.description === displayNoteData.description &&
                            values.title === (displayNoteData.title || '') &&
                            (values.is_private ?? false) === originalIsPrivate &&
                            (values.category || 'regular') === existingCategory &&
                            (values.note_template_id || '') === (existingTemplateId || '') &&
                            (() => {
                              const noteWithAudio = displayNoteData as NoteResponse;
                              const originalAudioAttachments = noteWithAudio?.audio_attachments || noteWithAudio?.attachments || [];
                              const originalKeys = originalAudioAttachments.map(att => att.key || (att.url ? extractKeyFromUrl(att.url) : '')).filter(Boolean);
                              const hasNewVoiceNotes = voiceNotes.some(vn => vn.audioUrl.startsWith('blob:'));
                              const currentKeys = voiceNotes.map(vn => {
                                const noteWithKey = vn as VoiceNote & { audioUrlKey?: string };
                                const urlOrKey = noteWithKey.audioUrlKey || (vn.audioUrl.startsWith('blob:') ? null : vn.audioUrl);
                                return urlOrKey ? extractKeyFromUrl(urlOrKey) : null;
                              }).filter((key): key is string => key !== null);

                              //   if (hasNewVoiceNotes) {
                              //     return false;
                              //   }

                              if (hasNewVoiceNotes) {
                                return false;
                              }
                              if (originalKeys.length !== currentKeys.length) {
                                return false;
                              }
                              return JSON.stringify([...originalKeys].sort()) === JSON.stringify([...currentKeys].sort());
                            })()
                          )
                        }
                        isLoading={loading}
                      />
                      {modalMode === 'update' && (
                        <div
                          className='delete-btn add-btn-white'
                          onClick={() =>
                            dispatch(changeDeleteNoteModalVisibility(true))
                          }
                        >
                          <TrashIcon /> Delete
                        </div>
                      )}
                      {modalError && (
                        <div className='error-msg'>
                          Error saving note, please try again.
                        </div>
                      )}
                    </div>
                  )}
                </form>
                <ConfirmModal
                  isOpen={isDeleteNoteModalOpened}
                  onClose={() => dispatch(changeDeleteNoteModalVisibility(false))}
                  onConfirmClick={onDeleteNote}
                  title='Delete this note?'
                  subtitle='This action will permanently remove the note. Once deleted, it cannot be restored.'
                  confirmBtnText='Delete'
                  cancelBtnText='Cancel'
                />
                <ConfirmModal
                  isOpen={isSaveDataNoteModalOpened}
                  onClose={() => {
                    dispatch(changeSaveDataNoteModalVisibility(false));
                    onClose();
                  }}
                  onConfirmClick={() => onSaveDataNote(values)}
                  title='Save Note?'
                  subtitle='You have unsaved data. Do you want to save this note?'
                  confirmBtnText='Yes'
                  cancelBtnText='No'
                />
                <ConfirmModal
                  isOpen={isDeleteVoiceNoteModalOpened}
                  onClose={() => {
                    setIsDeleteVoiceNoteModalOpened(false);
                    setVoiceNoteToDelete(null);
                  }}
                  onConfirmClick={confirmDeleteVoiceNote}
                  title='Delete this voice recording?'
                  subtitle='This action is permanent and cannot be undone.'
                  confirmBtnText='Delete'
                  cancelBtnText='Cancel'
                />
              </>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default MemberNotesModal;
